/**
 * Standard "select the call you're documenting" picker.
 *
 * Lists recent Dialpad calls between the current practitioner role and the
 * patient (never an inferred "most recent"), so the clinician explicitly picks
 * the call their care-time entry documents — guaranteeing the right inference
 * and giving the entry an auditable `Communication` link. Auto-polls (and
 * offers a manual refresh) so a call still being ingested after hang-up appears
 * without re-opening the form.
 *
 * Gated behind the `dialpadCallSelector` feature flag (off in production until
 * the manual-call-selection UX ships): when disabled the picker renders nothing
 * and issues no recent-call query, so every surface degrades cleanly to
 * manual-minutes entry. The care-time double-dipping hotfix is independent of
 * this picker.
 *
 * @agentic connections
 */

import { useEffect } from "react";
import {
  Anchor,
  Button,
  Group,
  Loader,
  Radio,
  Stack,
  Text,
} from "@mantine/core";
import { IconPhone, IconRefresh } from "@tabler/icons-react";
import {
  useRecentPatientCalls,
  type RecentCall,
} from "@/hooks/domain/useRecentPatientCalls";
import { useCurrentPractitionerRole } from "@/hooks/shared/useCurrentPractitionerRole";
import { useFeatureFlag } from "@/hooks/shared/useFeatureFlag";

/** Props for `DialpadCallPicker`. @agentic connections */
export type DialpadCallPickerProps = {
  patientId: string | undefined;
  /** Currently linked call, if any. */
  selectedCommunicationId?: string;
  /** Fired when the clinician selects a call, or `undefined` to clear. */
  onSelect: (call: RecentCall | undefined) => void;
  /** Practitioner role to scope calls to. Defaults to the current user's role. */
  practitionerRoleId?: string;
  /** Auto-poll while unselected to surface calls still being ingested. */
  autoPoll?: boolean;
};

const MAX_AUTO_POLLS = 6;
const POLL_INTERVAL_MS = 5000;

const formatCallTime = (iso: string): string => {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  return new Date(ms).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const directionLabel = (direction: RecentCall["direction"]): string => {
  if (direction === "inbound") return "Inbound";
  if (direction === "outbound") return "Outbound";
  return "Call";
};

const callLabel = (call: RecentCall): string =>
  `${directionLabel(call.direction)} · ${formatCallTime(call.callTime)} · ${call.minutes} min`;

/**
 * Recent-call selector for call-based clinical documentation.
 * @agentic connections
 */
export const DialpadCallPicker = ({
  patientId,
  selectedCommunicationId,
  onSelect,
  practitionerRoleId,
  autoPoll = true,
}: DialpadCallPickerProps) => {
  const selectorEnabled = useFeatureFlag("dialpadCallSelector");
  const { practitionerRole } = useCurrentPractitionerRole();
  const roleId = practitionerRoleId ?? practitionerRole?.id;
  // Skip the recent-call query entirely when the selector is flagged off.
  const { calls, loading, refetch } = useRecentPatientCalls(
    selectorEnabled ? patientId : undefined,
    roleId,
  );

  // Re-poll while no call is selected so a just-ended call (still being
  // ingested by the webhook) shows up without re-opening the form.
  useEffect(() => {
    if (!selectorEnabled) return;
    if (!autoPoll || selectedCommunicationId) return;
    if (!patientId || !roleId) return;
    let count = 0;
    const intervalId = setInterval(() => {
      count += 1;
      refetch();
      if (count >= MAX_AUTO_POLLS) clearInterval(intervalId);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [
    selectorEnabled,
    autoPoll,
    selectedCommunicationId,
    patientId,
    roleId,
    refetch,
  ]);

  if (!selectorEnabled) return null;
  if (!patientId) return null;

  return (
    <Stack gap={6} aria-label="Link a Dialpad call">
      <Group justify="space-between" align="center">
        <Group gap={6} align="center">
          <IconPhone size={14} />
          <Text size="xs" fw={600} c="dimmed">
            Link the Dialpad call you're documenting
          </Text>
        </Group>
        <Button
          variant="subtle"
          size="compact-xs"
          leftSection={<IconRefresh size={12} />}
          onClick={() => refetch()}
          loading={loading}
        >
          Refresh
        </Button>
      </Group>

      {loading && calls.length === 0 ? (
        <Group gap={6}>
          <Loader size="xs" />
          <Text size="xs" c="dimmed">
            Looking for recent calls…
          </Text>
        </Group>
      ) : calls.length === 0 ? (
        <Text size="xs" c="dimmed">
          No recent calls with this patient. Enter minutes manually, or refresh
          if a call just ended.
        </Text>
      ) : (
        <Radio.Group
          value={selectedCommunicationId ?? ""}
          onChange={(id) =>
            onSelect(calls.find((c) => c.communicationId === id))
          }
        >
          <Stack gap={4}>
            {calls.map((call) => (
              <Radio
                key={call.communicationId}
                value={call.communicationId}
                label={callLabel(call)}
                size="xs"
              />
            ))}
          </Stack>
        </Radio.Group>
      )}

      {selectedCommunicationId ? (
        <Anchor
          component="button"
          type="button"
          size="xs"
          onClick={() => onSelect(undefined)}
        >
          Clear linked call
        </Anchor>
      ) : null}
    </Stack>
  );
};
